import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  toast
} from "@veritrellis/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { WorkspaceRole } from "@veritrellis/shared/roles";
import { FormField } from "../FormField.js";
import { apiFetch } from "../../lib/api.js";
import { workspaceRoleDescriptions } from "../../lib/workspaceRolePresentation.js";

const isWorkspaceRole = (v: string): v is WorkspaceRole =>
  v === "owner" ||
  v === "admin" ||
  v === "developer" ||
  v === "approver" ||
  v === "viewer" ||
  v === "auditor";

type MembersInvitePanelProps = {
  workspaceId: string;
  inviteRoles: WorkspaceRole[];
};

export const MembersInvitePanel = ({ workspaceId, inviteRoles }: MembersInvitePanelProps) => {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const defaultRole = inviteRoles[0] ?? "viewer";
  const [role, setRole] = useState<WorkspaceRole>(defaultRole);
  const [emailError, setEmailError] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: async (values: { email: string; role: WorkspaceRole }) => {
      if (!inviteRoles.includes(values.role)) {
        throw new Error("invalid_role");
      }
      const res = await apiFetch(`/admin/workspaces/${workspaceId}/members`, {
        method: "POST",
        body: JSON.stringify({ email: values.email.trim().toLowerCase(), role: values.role })
      });
      if (res.status === 409) {
        throw new Error("already_member");
      }
      if (!res.ok) {
        throw new Error(`invite_${String(res.status)}`);
      }
    },
    onSuccess: () => {
      setEmail("");
      setRole(defaultRole);
      setEmailError(null);
      void queryClient.invalidateQueries({ queryKey: ["members", workspaceId] });
      toast.show({
        title: "Member added",
        message: "They can sign in with that email via your organization SSO.",
        color: "green"
      });
    },
    onError: (err: Error) => {
      const msg =
        err.message === "already_member"
          ? "That user is already in this workspace."
          : err.message === "invalid_role"
            ? "Pick a role you are allowed to assign."
            : err.message.includes("entitlement")
              ? "Member limit reached for your plan. Upgrade billing to add more seats."
              : "Could not add member.";
      toast.show({ title: "Error", message: msg, color: "red" });
    }
  });

  const roleForHelp: WorkspaceRole = inviteRoles.includes(role) ? role : defaultRole;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/u.test(email.trim())) {
      setEmailError("Valid email required");
      return;
    }
    setEmailError(null);
    invite.mutate({ email, role: inviteRoles.includes(role) ? role : defaultRole });
  };

  return (
    <section className="admin-panel" aria-labelledby="members-invite-heading">
      <div className="admin-panel-header">
        <h2 id="members-invite-heading" className="admin-panel-title">
          Invite member
        </h2>
      </div>
      <form onSubmit={handleSubmit} className="admin-panel-body-padded">
        <p className="admin-settings-row-desc" style={{ margin: "0 0 1.25rem" }}>
          We create the user if needed and grant workspace access. New users sign in with the same
          identity provider as your organization.
        </p>
        <div className="admin-form-field-row">
          <FormField width="full" className="admin-form-field-wide">
            <Label htmlFor="invite-email">Work email</Label>
            <Input
              id="invite-email"
              type="email"
              autoComplete="email"
              placeholder="colleague@company.com"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
            {emailError ? <p className="text-xs text-destructive">{emailError}</p> : null}
          </FormField>
          <FormField>
            <Label htmlFor="invite-role">Role</Label>
            <Select
              value={inviteRoles.includes(role) ? role : defaultRole}
              onValueChange={(v) => {
                if (isWorkspaceRole(v)) {
                  setRole(v);
                }
              }}
            >
              <SelectTrigger id="invite-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {inviteRoles.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <Button
            type="submit"
            disabled={invite.isPending}
            style={{ alignSelf: "flex-end", flexShrink: 0 }}
          >
            {invite.isPending ? <Spinner className="mr-2 h-4 w-4" /> : null}
            Send invite
          </Button>
        </div>
        <div className="admin-members-role-hint">
          <p className="admin-members-role-hint-label">{roleForHelp}</p>
          <p className="admin-members-role-hint-desc">{workspaceRoleDescriptions[roleForHelp]}</p>
        </div>
      </form>
    </section>
  );
};
